import { useState, useEffect, useCallback } from 'react';
import { FormGroup, Select } from '../../components/Form';
import { listarTicketsPendentes } from '../../services/ghlVendasTickets';
import { submitVendaTicket, getAgendadores, deleteTicketVenda } from '../../services/api';
import { logError } from '../../lib/logError';
import { EntradasSection } from './EntradasSection';
import { ParcelamentoSection } from './ParcelamentoSection';
import { hojeSP } from '../../lib/tz';


const formatCurrency = (val) => {
    let raw = val.replace(/\D/g, '');
    if (raw === '') return '';
    raw = (parseInt(raw, 10) / 100).toFixed(2);
    return raw.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseCurrencyToFloat = (val) => {
    if (!val) return 0;
    return parseFloat(val.replace(/\./g, '').replace(',', '.')) || 0;
};

export const CompletarVendaGhlTab = ({ currentUser, role }) => {
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [ticketSelecionado, setTicketSelecionado] = useState(null);
    const [gestoresComerciais, setGestoresComerciais] = useState([]);
    const [temGestorComercial, setTemGestorComercial] = useState(false);
    const [gestorComercial, setGestorComercial] = useState('');
    const [entradas, setEntradas] = useState([{ valor: '', formaPagamento: '', data: hojeSP() }]);
    const [valorParcelar, setValorParcelar] = useState('');
    const [parcelas, setParcelas] = useState('');
    const [formaPagamentoParcelas, setFormaPagamentoParcelas] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // useCallback com as MESMAS deps do efeito abaixo: a referência só muda quando role ou
    // o nome do usuário mudam, que é exatamente quando os tickets precisam ser recarregados.
    // Sem isso a função é recriada a cada render e o efeito fica com dependência faltando.
    const carregarTickets = useCallback(() => {
        setLoadingTickets(true);
        listarTicketsPendentes({ role, nomeUsuario: currentUser?.nome })
            .then(setTickets)
            .catch(err => { logError(err, { page: 'CompletarVendaGhlTab' }); setErrorMessage('Não foi possível carregar os tickets pendentes.'); })
            .finally(() => setLoadingTickets(false));
    }, [role, currentUser?.nome]);

    const handleDeleteTicket = async (e, opp, nome) => {
        e.stopPropagation();
        if (!confirm(`Apagar o ticket de venda de ${nome}? Remove a venda, o ticket e o evento de origem. Ação de gestor.`)) return;
        setErrorMessage('');
        try {
            await deleteTicketVenda({ ghl_opportunity_id: opp });
            carregarTickets();
        } catch (err) {
            logError(err, { fn: 'handleDeleteTicket', page: 'CompletarVendaGhlTab' });
            setErrorMessage(err?.message || 'Erro ao apagar ticket.');
        }
    };

    useEffect(() => {
        if (!role) return;
        carregarTickets();
        getAgendadores()
            .then(data => setGestoresComerciais(data.filter(m => m.cargo?.trim().toLowerCase() === 'gestor comercial')))
            .catch(() => {});
    }, [role, carregarTickets]);

    const handleEntradaChange = (idx, field, value) => {
        setEntradas(prev => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [field]: field === 'valor' ? formatCurrency(value) : value };
            return updated;
        });
    };
    const addEntrada = () => setEntradas(prev => [...prev, { valor: '', formaPagamento: '', data: hojeSP() }]);
    const removeEntrada = (idx) => setEntradas(prev => prev.filter((_, i) => i !== idx));

    const valorEntradaTotal = entradas.reduce((sum, e) => sum + parseCurrencyToFloat(e.valor), 0);
    const valorTotalPreenchido = valorEntradaTotal + parseCurrencyToFloat(valorParcelar);

    const voltarParaLista = () => {
        setTicketSelecionado(null);
        setTemGestorComercial(false);
        setGestorComercial('');
        setEntradas([{ valor: '', formaPagamento: '', data: hojeSP() }]);
        setValorParcelar('');
        setParcelas('');
        setFormaPagamentoParcelas('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const entradasFilled = entradas.some(en => parseCurrencyToFloat(en.valor) > 0);
        if (!entradasFilled && !parseCurrencyToFloat(valorParcelar)) {
            setErrorMessage('Informe ao menos uma entrada com valor ou um valor a parcelar.');
            return;
        }
        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            await submitVendaTicket({
                ticket: ticketSelecionado,
                gestorComercial: temGestorComercial ? gestorComercial : '',
                temGestorComercial,
                entradas: entradas.map(en => ({
                    valor: parseCurrencyToFloat(en.valor),
                    formaPagamento: en.formaPagamento || 'Nao Informado',
                    data: en.data || hojeSP(),
                })),
                valorParcelar: parseCurrencyToFloat(valorParcelar),
                parcelas: parseInt(parcelas, 10) || 0,
                formaPagamentoParcelas,
                completadoPor: currentUser?.email ?? null,
            });
            setSuccessMessage('Venda registrada e encaminhada para aprovação do Orbion Finance.');
            voltarParaLista();
            carregarTickets();
        } catch (error) {
            if (error?.code === 'TICKET_JA_COMPLETO') {
                // A mensagem vem do serviço: "já foi completado" ou "está sendo completado" —
                // são situações diferentes para quem está na tela.
                setErrorMessage(error.message || 'Este ticket já foi completado por outra pessoa. Atualize a lista.');
                voltarParaLista();
                carregarTickets();
            } else {
                logError(error, { fn: 'handleSubmit', page: 'CompletarVendaGhlTab' });
                setErrorMessage(error?.message || 'Erro ao registrar venda.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loadingTickets) return <p>Carregando tickets...</p>;

    if (!ticketSelecionado) {
        return (
            <div className="card">
                <p className="section-label">Vendas pendentes de completar</p>
                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {errorMessage && <div className="alert alert-error">{errorMessage}</div>}
                {tickets.length === 0 && <p style={{ color: 'var(--muted)' }}>Nenhuma venda pendente no momento.</p>}
                {tickets.map(t => (
                    <div
                        key={t.ghl_opportunity_id}
                        onClick={() => setTicketSelecionado(t)}
                        style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '0.9rem 1rem', borderRadius: '10px', cursor: 'pointer',
                            border: `1px solid ${t.destaque ? 'var(--danger-soft)' : 'var(--glass-border)'}`,
                            background: t.destaque ? 'rgba(220, 38, 38, 0.06)' : 'var(--canvas)',
                            marginBottom: '0.6rem',
                        }}
                    >
                        <div>
                            <p style={{ margin: 0, fontWeight: 600 }}>{t.contato_nome} — {t.produto}</p>
                            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8125rem', color: 'var(--muted)' }}>
                                R$ {(t.valor_venda ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} · há {t.dias_sem_completar} dia(s)
                            </p>
                            {t.motivo_rejeicao && (
                                <p style={{ margin: '0.3rem 0 0', fontSize: '0.8125rem', color: 'var(--danger)' }}>
                                    Rejeitado: {t.motivo_rejeicao}
                                </p>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                            {t.destaque && <span className="pill pill-ghost pill-sm" style={{ color: 'var(--danger)' }}>Atenção</span>}
                            {role === 'gestor' && (
                                <button
                                    type="button"
                                    onClick={(e) => handleDeleteTicket(e, t.ghl_opportunity_id, t.contato_nome)}
                                    style={{ background: 'none', border: '1px solid var(--glass-border)', borderRadius: '7px', padding: '0.3rem 0.6rem', color: 'var(--danger-soft)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                                    title="Apagar ticket (venda + ticket + evento)"
                                >
                                    Apagar
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {errorMessage && <div className="alert alert-error">{errorMessage}</div>}

            <div className="card" style={{ marginBottom: '1.25rem' }}>
                <p className="section-label">Dados da venda (vindos do GHL)</p>
                <div className="grid-2">
                    <FormGroup label="Cliente"><p>{ticketSelecionado.contato_nome}</p></FormGroup>
                    <FormGroup label="Produto"><p>{ticketSelecionado.produto}</p></FormGroup>
                    <FormGroup label="Valor total"><p>R$ {(ticketSelecionado.valor_venda ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></FormGroup>
                    <FormGroup label="Pré-vendedor"><p>{ticketSelecionado.sdr_nome}</p></FormGroup>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                        <input
                            type="checkbox"
                            checked={temGestorComercial}
                            onChange={e => { setTemGestorComercial(e.target.checked); if (!e.target.checked) setGestorComercial(''); }}
                            style={{ width: '16px', height: '16px', accentColor: 'var(--emerald)', cursor: 'pointer', flexShrink: 0 }}
                        />
                        Teve Gestor Comercial?
                    </label>
                </div>
                {temGestorComercial && (
                    <FormGroup label="Gestor Comercial" htmlFor="gestorComercial" style={{ marginTop: '0.75rem' }}>
                        <Select id="gestorComercial" value={gestorComercial} onChange={e => setGestorComercial(e.target.value)} required>
                            <option value="">Selecione o gestor</option>
                            {gestoresComerciais.map(g => <option key={g.nome} value={g.nome}>{g.nome}</option>)}
                        </Select>
                    </FormGroup>
                )}
            </div>

            <EntradasSection entradas={entradas} onChange={handleEntradaChange} onAdd={addEntrada} onRemove={removeEntrada} />
            <ParcelamentoSection
                formData={{ valorParcelar, parcelas, formaPagamentoParcelas }}
                onChange={e => {
                    const { name, value } = e.target;
                    if (name === 'valorParcelar') setValorParcelar(formatCurrency(value));
                    else if (name === 'parcelas') setParcelas(value);
                    else if (name === 'formaPagamentoParcelas') setFormaPagamentoParcelas(value);
                }}
                valorTotal={valorTotalPreenchido}
                isSubmitting={isSubmitting}
                submitLabel="Confirmar e Enviar para Aprovação"
            />

            <button type="button" className="pill pill-ghost pill-sm" style={{ marginTop: '0.75rem' }} onClick={voltarParaLista}>
                Cancelar
            </button>
        </form>
    );
};
